import { apiClient } from '../../../lib/api/client';
import type { TaskItemListItem } from '../types';

/** Task counts tied to the reporting period when a range is selected (see dashboard subtitle). */
export interface TaskDashboardTaskMetrics {
  totalInReportingPeriod: number;
  openStatusCount: number;
  inProgressCount: number;
  blockedCount: number;
  completedInPeriodCount: number;
  cancelledInPeriodCount: number;
  followupOpenCount: number;
  internalOpenCount: number;
  completedFollowupsInPeriod: number;
  completedInternalInPeriod: number;
  overdueCount: number;
}

export interface TaskDashboardOperationalBucket {
  count: number;
  items: TaskItemListItem[];
}

export interface TaskDashboardOperational {
  overdue: TaskDashboardOperationalBucket;
  dueToday: TaskDashboardOperationalBucket;
  upcomingWeek: TaskDashboardOperationalBucket;
  otherOpen: TaskDashboardOperationalBucket;
}

export interface TaskDashboardStageRow {
  id: number;
  label: string;
  sortOrder: number;
  openDealCount: number;
  targetedAmountSum: number;
}

export interface TaskDashboardResponse {
  taskMetrics: TaskDashboardTaskMetrics;
  stage: TaskDashboardStageRow[];
  operational: TaskDashboardOperational;
}

export interface GetTaskDashboardParams {
  fromUtc?: string;
  toUtc?: string;
  segmentIds?: string[];
  contactCategoryIds?: number[];
  assigneeIds?: string[];
  timeZoneOffsetMinutes?: number;
}

export async function getTaskDashboard(
  params?: GetTaskDashboardParams
): Promise<TaskDashboardResponse> {
  const searchParams = new URLSearchParams();
  if (params?.fromUtc) searchParams.set('fromUtc', params.fromUtc);
  if (params?.toUtc) searchParams.set('toUtc', params.toUtc);
  if (params?.segmentIds?.length) {
    for (const id of params.segmentIds) searchParams.append('segmentIds', id);
  }
  if (params?.contactCategoryIds?.length) {
    for (const id of params.contactCategoryIds) searchParams.append('contactCategoryIds', String(id));
  }
  if (params?.assigneeIds?.length) {
    for (const id of params.assigneeIds) searchParams.append('assigneeIds', id);
  }
  if (params?.timeZoneOffsetMinutes != null) {
    searchParams.set('timeZoneOffsetMinutes', String(params.timeZoneOffsetMinutes));
  }
  const response = await apiClient.get<TaskDashboardResponse>(
    `/dashboard/task-dashboard?${searchParams.toString()}`
  );
  return response.data;
}

export async function exportTaskDashboard(
  params?: GetTaskDashboardParams
): Promise<Blob> {
  const searchParams = new URLSearchParams();
  if (params?.fromUtc) searchParams.set('fromUtc', params.fromUtc);
  if (params?.toUtc) searchParams.set('toUtc', params.toUtc);
  if (params?.segmentIds?.length) {
    for (const id of params.segmentIds) searchParams.append('segmentIds', id);
  }
  if (params?.contactCategoryIds?.length) {
    for (const id of params.contactCategoryIds) searchParams.append('contactCategoryIds', String(id));
  }
  if (params?.assigneeIds?.length) {
    for (const id of params.assigneeIds) searchParams.append('assigneeIds', id);
  }
  if (params?.timeZoneOffsetMinutes != null) {
    searchParams.set('timeZoneOffsetMinutes', String(params.timeZoneOffsetMinutes));
  }
  const response = await apiClient.get<Blob>(
    `/dashboard/task-dashboard/export?${searchParams.toString()}`,
    { responseType: 'blob' }
  );
  return response.data;
}

export async function exportTaskDashboardPdf(
  params?: GetTaskDashboardParams
): Promise<Blob> {
  const searchParams = new URLSearchParams();
  if (params?.fromUtc) searchParams.set('fromUtc', params.fromUtc);
  if (params?.toUtc) searchParams.set('toUtc', params.toUtc);
  if (params?.segmentIds?.length) {
    for (const id of params.segmentIds) searchParams.append('segmentIds', id);
  }
  if (params?.contactCategoryIds?.length) {
    for (const id of params.contactCategoryIds) searchParams.append('contactCategoryIds', String(id));
  }
  if (params?.assigneeIds?.length) {
    for (const id of params.assigneeIds) searchParams.append('assigneeIds', id);
  }
  if (params?.timeZoneOffsetMinutes != null) {
    searchParams.set('timeZoneOffsetMinutes', String(params.timeZoneOffsetMinutes));
  }
  const response = await apiClient.get<Blob>(
    `/dashboard/task-dashboard/export/pdf?${searchParams.toString()}`,
    { responseType: 'blob' }
  );
  return response.data;
}
